import { useCallback, useMemo, useRef, useState } from 'react';
import { useInView } from './useInView';
import { createEventPayload } from './GTM/useSendGTMEventOnce';
import { useGTMDataLayer } from './GTM/useGTMDataLayer';
import { queueGTMEvent } from './GTM/queueGTMEvent';
import { EventPayload } from '../../../types/GTM/EventPayload';
import { NAFGTMEvent } from '../../../types/GTM/NAFGTMEvent';
import { EligibleProduct } from '../components/MembershipBox/productUtils';

const CHECKOUT_REGISTRATION_METHOD_KEY = 'checkout_registration_method';

type TrackProductItemOptions = {
  product?: EligibleProduct;
  category2?: string;
  category3?: string;
};

export type PromotionData = {
  creative_slot: string;
  creative_name: string;
  promotion_id: string;
  promotion_name: string;
};

const getProductValue = (price: string, quantity: number): number | undefined => {
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice) || quantity <= 0) {
    return undefined;
  }

  return Number((numericPrice * quantity).toFixed(2));
};

const getProductPrice = (product?: EligibleProduct, quantity = 1): string | null => {
  const quantityKey = String(quantity);
  const quantityPrice = product?.productAppPrice?.[quantityKey]?.itemTotalPrice;
  if (typeof quantityPrice === 'number' && quantity > 0) {
    return (quantityPrice / quantity).toFixed(2);
  }

  const price = product?.productAppPrice?.['1']?.itemTotalPrice;
  return typeof price === 'number' ? price.toFixed(2) : null;
};

const buildItemPayload = (
  product: EligibleProduct,
  price: string,
  quantity = 1,
  category2?: string,
  category3?: string,
  promotionData?: PromotionData,
) => ({
  item_id: product.productNumber,
  item_name: product.productName || product.productNumber,
  price,
  quantity: String(quantity),
  item_category2: category2,
  item_category3: category3,
  ...promotionData,
});

const buildEventPayload = (
  event: NAFGTMEvent,
  product?: EligibleProduct,
  price?: string | null,
  category2?: string,
  category3?: string,
  quantity = 1,
  value?: number,
  promotionData?: PromotionData,
  checkoutRegistrationMethod?: EventPayload['checkout_registration_method'],
  campaignCode?: string,
) => {
  if (!product || !price) {
    return undefined;
  }

  return createEventPayload({
    event,
    ...(checkoutRegistrationMethod && { [CHECKOUT_REGISTRATION_METHOD_KEY]: checkoutRegistrationMethod }),
    ...(campaignCode && { campaign_code: campaignCode }),
    ecommerce: {
      ...(typeof value === 'number' ? { value } : {}),
      items: [buildItemPayload(product, price, quantity, category2, category3, promotionData)],
    },
  });
};

export const useTrackViewItem = ({ product, category2, category3 }: TrackProductItemOptions) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(wrapperRef, { triggerOnMount: true });
  const productPrice = useMemo(() => getProductPrice(product), [product]);
  const [alreadyTracked, setAlreadyTracked] = useState(false);
  const dataLayer = useGTMDataLayer();

  const viewItemEvent = useMemo(() => {
    if (!dataLayer || !product || !isInView || alreadyTracked) {
      return undefined;
    }

    setAlreadyTracked(true);

    return buildEventPayload(NAFGTMEvent.viewItem, product, productPrice, category2, category3);
  }, [dataLayer, isInView, product, productPrice, category2, category3, alreadyTracked]);

  if (viewItemEvent) {
    queueGTMEvent(dataLayer, viewItemEvent);
  }

  return { wrapperRef };
};

export const useTrackSelectItem = ({ product, category2, category3 }: TrackProductItemOptions) => {
  const dataLayer = useGTMDataLayer();
  const productPrice = useMemo(() => getProductPrice(product), [product]);

  const trackSelectItem = useCallback(() => {
    const event = buildEventPayload(NAFGTMEvent.selectItem, product, productPrice, category2, category3);
    if (!event || !dataLayer) {
      return;
    }

    dataLayer.push(event);
  }, [dataLayer, product, productPrice, category2, category3]);

  return { trackSelectItem };
};

export const useTrackProductsCart = (
  checkoutRegistrationMethod?: EventPayload['checkout_registration_method'],
  campaignCode?: string,
) => {
  const dataLayer = useGTMDataLayer();

  const trackAddToCart = useCallback(
    (product: EligibleProduct, category2?: string, category3?: string, quantity = 1, promotionData?: PromotionData) => {
      if (!dataLayer || !product || quantity <= 0) {
        return;
      }

      const price = getProductPrice(product, quantity);
      const value = price ? getProductValue(price, quantity) : undefined;
      const event = buildEventPayload(
        NAFGTMEvent.addToCart,
        product,
        price,
        category2,
        category3,
        quantity,
        value,
        promotionData,
        checkoutRegistrationMethod,
        campaignCode,
      );
      if (!event) {
        return;
      }

      dataLayer.push(event);
    },
    [dataLayer, checkoutRegistrationMethod, campaignCode],
  );

  const trackRemoveFromCart = useCallback(
    (product: EligibleProduct, category2?: string, category3?: string, quantity = 1, promotionData?: PromotionData) => {
      if (!dataLayer || !product || quantity <= 0) {
        return;
      }

      const price = getProductPrice(product, quantity);
      const value = price ? getProductValue(price, quantity) : undefined;
      const event = buildEventPayload(
        NAFGTMEvent.removeFromCart,
        product,
        price,
        category2,
        category3,
        quantity,
        value,
        promotionData,
        checkoutRegistrationMethod,
        campaignCode,
      );
      if (!event) {
        return;
      }

      dataLayer.push(event);
    },
    [dataLayer, checkoutRegistrationMethod, campaignCode],
  );

  return { trackAddToCart, trackRemoveFromCart };
};
